/**
 * Biome-varying weather v2 (2026-08-06) — a pure, deterministic METEOROLOGICAL
 * resolver: temperature / humidity fields produce the weather instead of a
 * global scalar drift.
 *
 * The pipeline (all pure functions of shared data — seed, position, wall
 * clock, time-of-day — so every client computes IDENTICAL weather with zero
 * server state):
 *
 *   1. CLIMATE      — per-biome baselines (base temp, diurnal swing, base RH)
 *                     blended by the Phase A biomeWeights at (x, z).
 *   2. ALTITUDE     — environmental lapse rate: −6.5 °C per km of elevation
 *                     (standard atmosphere; measured real-world range
 *                     0.45–0.8 °C/100 m).
 *   3. DIURNAL      — temperature peaks mid-afternoon (~14:30) and bottoms
 *                     before dawn; RH runs opposite (nights more humid).
 *   4. SYNOPTIC     — two rotated value-noise fields (6 km + 2.2 km, the
 *                     anti-grid rotation toolkit) ADVECTED along a per-seed
 *                     prevailing wind at ~11 m/s — humidity/temperature
 *                     anomalies sweep across the land like real fronts
 *                     (~40 km/h), so weather belongs to AREAS and moves.
 *   5. OROGRAPHIC   — air forced up a windward slope moistens (+RH), the lee
 *                     side sits in a rain shadow (−RH) — sampled from the
 *                     terrain gradient along the wind.
 *   6. DERIVED      — cloud cover from RH; precipitation above an RH ramp;
 *                     rain vs snow by a wet-bulb-ish temperature (research:
 *                     the 50/50 air-temp threshold averages ~+1.0 °C and
 *                     humidity-aware splits beat air-temp-only); storms need
 *                     heat + moisture (CAPE-lite instability); radiation fog
 *                     needs near-saturation + calm air, peaking around dawn;
 *                     dew point via the Magnus formula (a 17.62, b 243.12 —
 *                     accurate to ±0.35 °C over −45..60 °C).
 *
 * Live-ops (skyState.weather) and debug overrides PIN the derived outputs —
 * a forced 'snow' event snows on the plains regardless of the field.
 */
import type { Biome } from '@shared/terrain.js';
import { biomeWeights, terrainHeight } from '@shared/terrain.js';

export interface BiomeWeather {
  cloudCoverage: number;   // 0..1 baseline coverage (the "partly cloudy" floor)
  cloudDensity: number;    // cloud opacity multiplier (~0.6..1.4)
  cloudTintHex: string;    // cloud light tint
  fogDensity: number;      // FogExp2 density baseline — fogV(visibility km), THE VISIBILITY LAW
  particleKind: 'none' | 'rain' | 'snow'; // legacy tendency (kind now derives from temperature)
  particleDensity: number; // legacy tendency scale
  wetness: number;         // 0..1 surface wetness floor
  windStrength: number;    // foliage/particle wind (NOT cloud drift)
  /** Climate: mean 2 m air temperature at sea level (°C). */
  baseTempC: number;
  /** Climate: full day-night temperature swing (°C). */
  diurnalSwingC: number;
  /** Climate: mean relative humidity 0..1. */
  baseRH: number;
}

/**
 * THE VISIBILITY LAW (2026-09-27, the coordinator's realism review — realism-additions §0.1): fog is written as a
 * meteorological VISIBILITY and converted here. three's FogExp2 attenuates by exp(−(ρ·d)²), so Koschmieder's 2 %
 * contrast falls at ρ·V = √(−ln 0.02) = 1.978. The old floors (0.002–0.014/m) were a 140–990 m visibility — a
 * FOG by definition, on every clear day, and the reason the far tier was a wall of haze past a kilometre.
 * Real clear air: 30–100 km (a desert 60+, a mountain day 30, a humid marsh 8–15).
 */
export function fogDensityForVisibilityKm(visibilityKm: number): number {
  return 1.978 / (Math.max(0.05, visibilityKm) * 1000);
}
const fogV = fogDensityForVisibilityKm;

// Starting personalities (tunable in-browser). Desert driest → wetland wettest.
// Coverages are deliberately LOW so the default sky reads "partly cloudy" — the
// cloud renderer's coverage knob carves clear gaps out of the weather map, so a
// high coverage here produces a solid overcast deck (the uniform-band look).
// Climate columns feed the meteo field: deserts run hot/dry with a huge diurnal
// swing, highlands cold (the snow line), wetlands warm and near-saturated.
export const BIOME_WEATHER: Record<Biome, BiomeWeather> = {
  dune_desert:     { cloudCoverage: 0.04, cloudDensity: 0.6, cloudTintHex: '#e8d8b0', fogDensity: fogV(60), particleKind: 'none', particleDensity: 0.0, wetness: 0.00, windStrength: 0.45, baseTempC: 33, diurnalSwingC: 16, baseRH: 0.14 },
  crystal_forest:  { cloudCoverage: 0.12, cloudDensity: 0.8, cloudTintHex: '#dfe8f0', fogDensity: fogV(40), particleKind: 'none', particleDensity: 0.0, wetness: 0.10, windStrength: 0.20, baseTempC: 15, diurnalSwingC: 8,  baseRH: 0.55 },
  regolith_plains: { cloudCoverage: 0.18, cloudDensity: 1.0, cloudTintHex: '#d8dadf', fogDensity: fogV(35), particleKind: 'rain', particleDensity: 0.3, wetness: 0.20, windStrength: 0.30, baseTempC: 18, diurnalSwingC: 9,  baseRH: 0.55 },
  rocky_highlands: { cloudCoverage: 0.32, cloudDensity: 1.4, cloudTintHex: '#9aa2ad', fogDensity: fogV(30), particleKind: 'snow', particleDensity: 0.7, wetness: 0.40, windStrength: 0.60, baseTempC: 3,  diurnalSwingC: 7,  baseRH: 0.66 },
  moss_wetland:    { cloudCoverage: 0.38, cloudDensity: 1.3, cloudTintHex: '#b6bcc0', fogDensity: fogV(12), particleKind: 'rain', particleDensity: 0.9, wetness: 0.70, windStrength: 0.25, baseTempC: 21, diurnalSwingC: 5,  baseRH: 0.86 },
  fungal_basin:    { cloudCoverage: 0.20, cloudDensity: 1.0, cloudTintHex: '#aebfa0', fogDensity: fogV(8), particleKind: 'none', particleDensity: 0.0, wetness: 0.30, windStrength: 0.10, baseTempC: 23, diurnalSwingC: 4,  baseRH: 0.74 },
};

export const DRIFT_PERIOD_SEC = 1800; // legacy export (harness/back-compat)

/** Legacy global drift 0..1 — kept for back-compat; the resolver now derives
 *  its variation from the ADVECTED synoptic field instead. */
export function weatherDrift(seed: string, t: number): number {
  const s = hashSeed(seed);
  const a = Math.sin((t / DRIFT_PERIOD_SEC + s) * Math.PI * 2);
  const b = Math.sin((t / (DRIFT_PERIOD_SEC * 0.6) + s * 1.7) * Math.PI * 2);
  return clamp01(0.5 + 0.5 * (0.6 * a + 0.4 * b));
}

// ─── The meteorological field ─────────────────────────────────────────────────

/** Fronts advect at ~11 m/s (~40 km/h — the speed of a real mid-latitude front):
 *  standing still, the weather turns over in ~10–30 min; walking ~2 km can
 *  cross a front edge. */
export const FRONT_ADVECT_M_PER_S = 11;
/** Primary / secondary synoptic cell wavelengths (m). */
export const SYNOPTIC_WAVELENGTH_M = 6000;
const SYNOPTIC_WAVELENGTH2_M = 2200;
/** Environmental lapse rate (°C per m). */
const LAPSE_C_PER_M = 6.5 / 1000;
/** Rain/snow split on a wet-bulb-ish temperature (°C). */
const SNOW_WETBULB_C = 0.9;

export interface Meteo {
  /** 2 m air temperature (°C) at (x, z). */
  temperatureC: number;
  /** Relative humidity 0..1. */
  humidity01: number;
  /** Dew point (°C, Magnus). */
  dewPointC: number;
  /** Wet-bulb-ish temperature used for the rain/snow split (°C). */
  wetBulbC: number;
  /** Precipitation intensity 0..1 at this position. */
  precip01: number;
  kind: 'none' | 'rain' | 'snow';
  /** CAPE-lite storm proxy 0..1 (heat × moisture). */
  instability01: number;
  /** Radiation/advection fog factor 0..1. */
  fog01: number;
  /** Cloud cover 0..1 (before the biome aesthetic floor mapping). */
  cover01: number;
  /** Wind strength 0..1 and direction (unit vector). */
  wind01: number;
  windDirX: number;
  windDirZ: number;
  /** Blowing dust 0..1 — a sandstorm front over dry ground (2026-09-11). */
  dust01: number;
  /** How much desert lies under (x, z): 0..1 (dune 1, regolith 0.15). Dust never leaves it. */
  dustW: number;
}

/** Sandstorm fronts: a slow gust field advected with the moisture fronts (a haboob rides a gust front for hours). */
const DUST_WAVELENGTH_M = 26000;

/** Per-seed prevailing wind direction (unit) — rotates with the seed hash. */
export function prevailingWind(seed: string): { x: number; z: number } {
  const a = hashSeed(seed) * Math.PI * 2;
  return { x: Math.cos(a), z: Math.sin(a) };
}

/**
 * The meteorological state at world (x, z) at shared time tSec (wall-clock
 * seconds — every client must pass the same clock) and time-of-day tod (0..1,
 * 0 = midnight). Pure and deterministic.
 */
export function meteoAt(seed: string, x: number, z: number, tSec: number, tod: number): Meteo {
  const w = biomeWeights(seed, x, z);
  let baseT = 0, swing = 0, baseRH = 0, baseWind = 0;
  for (const b of Object.keys(w) as Biome[]) {
    const bw = w[b], p = BIOME_WEATHER[b];
    baseT += p.baseTempC * bw;
    swing += p.diurnalSwingC * bw;
    baseRH += p.baseRH * bw;
    baseWind += p.windStrength * bw;
  }

  // Synoptic anomalies: rotated noise domains advected along the prevailing
  // wind — the SAME pattern arrives downwind later (fronts MOVE).
  const wind = prevailingWind(seed);
  const ax = x - wind.x * FRONT_ADVECT_M_PER_S * tSec;
  const az = z - wind.z * FRONT_ADVECT_M_PER_S * tSec;
  // Anti-grid: rotate the two octaves against each other (27° / −14°).
  const r1x = ax * 0.891 - az * 0.454, r1z = ax * 0.454 + az * 0.891;
  const r2x = ax * 0.970 + az * 0.242, r2z = -ax * 0.242 + az * 0.970;
  const n1 = vnoise(seed, r1x / SYNOPTIC_WAVELENGTH_M, r1z / SYNOPTIC_WAVELENGTH_M, 'wh');
  const n2 = vnoise(seed, r2x / SYNOPTIC_WAVELENGTH2_M, r2z / SYNOPTIC_WAVELENGTH2_M, 'wh2');
  const hAnom = ((n1 * 0.72 + n2 * 0.28) * 2 - 1);                    // −1..1 moisture anomaly
  const tAnom = (vnoise(seed, (ax + 31337) / 9000, (az - 71993) / 9000, 'wt') * 2 - 1) * 3.5; // ±3.5 °C air-mass anomaly

  // Diurnal: peak ~14:30 (tod 0.604), trough before dawn. RH runs opposite.
  const diurnal = Math.cos((tod - 0.604) * Math.PI * 2); // 1 at mid-afternoon
  const night = clamp01(Math.cos(tod * Math.PI * 2) * 0.5 + 0.5);  // 1 at midnight (tod 0), 0 at noon

  // Altitude + orographic lift (windward +RH, lee rain shadow −RH).
  const elev = Math.max(0, terrainHeight(seed, x, z));
  const hUp = terrainHeight(seed, x - wind.x * 150, z - wind.z * 150);
  const hDown = terrainHeight(seed, x + wind.x * 150, z + wind.z * 150);
  const uplift = (hDown - hUp) / 300; // terrain rising ALONG the wind = windward
  const orographic = Math.max(-0.16, Math.min(0.2, uplift * 2.2));

  const temperatureC = baseT + tAnom + diurnal * swing * 0.5 - elev * LAPSE_C_PER_M;
  const humidity01 = clamp01(
    baseRH
    + hAnom * 0.26
    + orographic
    + night * 0.1                       // nights run more humid
    - Math.max(0, temperatureC - 30) * 0.008, // hot afternoons dry out
  );

  // Derived condition chain.
  const cover01 = clamp01(smooth(0.35, 0.95, humidity01) + hAnom * 0.06);
  let precip01 = smooth(0.74, 0.96, humidity01) * (cover01 > 0.3 ? 1 : 0);
  const wetBulbC = temperatureC - (1 - humidity01) * 4.2; // cheap Stull-style depression
  const instability01 = clamp01((temperatureC - 21) / 13) * humidity01;
  if (precip01 > 0.55 && instability01 > 0.5) precip01 = Math.min(1, precip01 * 1.35); // convective burst
  const kind: Meteo['kind'] = precip01 <= 0.02 ? 'none' : wetBulbC <= SNOW_WETBULB_C ? 'snow' : 'rain';
  let wind01 = clamp01(baseWind * (0.55 + Math.abs(hAnom) * 0.7) + precip01 * 0.25 + instability01 * 0.15);
  // DUST (2026-09-11, sandstorms "in the right biomes"): only where the ground
  // is desert (dune 1, regolith 0.3), only in a dry air mass, only on a gusty
  // front — a rare, hours-long event that arrives and leaves with the fronts.
  const dustW = clamp01((w.dune_desert ?? 0) + (w.regolith_plains ?? 0) * 0.15); // gravel plains raise a light dust haze, never a sand wall
  const gust = vnoise(seed, (ax - 40411) / DUST_WAVELENGTH_M, (az + 20177) / DUST_WAVELENGTH_M, 'dust');
  const dust01 = dustW * smooth(0.6, 0.86, gust) * (1 - smooth(0.2, 0.45, humidity01)) * smooth(0.25, 0.7, wind01 + 0.25 * dustW);
  wind01 = clamp01(wind01 + dust01 * 0.45); // the storm IS wind
  // Radiation fog: near-saturation + calm air, peaking around dawn (tod ~0.23).
  const dawn = Math.exp(-Math.pow((tod - 0.23) / 0.07, 2));
  const fog01 = smooth(0.9, 1.0, humidity01 + dawn * 0.06) * (1 - wind01 * 0.7);
  const dewPointC = dewPoint(temperatureC, humidity01);

  return {
    temperatureC, humidity01, dewPointC, wetBulbC,
    precip01, kind, instability01, fog01, cover01,
    wind01, windDirX: wind.x, windDirZ: wind.z,
    dust01, dustW,
  };
}

// ─── Map-overlay helpers (pure — the World Atlas weather layers) ─────────────

/** The seven render-facing weather classes (matches the live-ops WEATHERS list). */
export type WeatherClass = 'clear' | 'cloudy' | 'fog' | 'rain' | 'storm' | 'snow' | 'sandstorm';

/** Classify a meteo sample into the map/legend weather class. */
export function classifyMeteo(m: Meteo): WeatherClass {
  if (m.precip01 > 0.02) {
    if (m.kind === 'snow') return 'snow';
    return m.precip01 >= 0.85 ? 'storm' : 'rain';
  }
  if (m.dust01 > 0.25) return 'sandstorm';
  if (m.fog01 > 0.45) return 'fog';
  if (m.cover01 > 0.55) return 'cloudy';
  return 'clear';
}

/** Meteorological temperature ramp (cold purple/blue → cyan at 0 °C → green →
 *  yellow → orange → hot red — the standard weather-map convention). */
const TEMP_STOPS: Array<[number, [number, number, number]]> = [
  [-25, [75, 46, 131]],   // deep cold purple
  [-10, [36, 81, 179]],   // blue
  [0,   [57, 193, 224]],  // cyan (freezing)
  [10,  [76, 175, 110]],  // green (mild)
  [20,  [232, 213, 74]],  // yellow (warm)
  [30,  [232, 143, 42]],  // orange (hot)
  [40,  [201, 42, 42]],   // red (scorching)
];
export function tempColor(tC: number): [number, number, number] {
  if (tC <= TEMP_STOPS[0][0]) return TEMP_STOPS[0][1];
  for (let i = 1; i < TEMP_STOPS.length; i++) {
    if (tC <= TEMP_STOPS[i][0]) {
      const [t0, c0] = TEMP_STOPS[i - 1];
      const [t1, c1] = TEMP_STOPS[i];
      const f = (tC - t0) / (t1 - t0);
      return [c0[0] + (c1[0] - c0[0]) * f, c0[1] + (c1[1] - c0[1]) * f, c0[2] + (c1[2] - c0[2]) * f];
    }
  }
  return TEMP_STOPS[TEMP_STOPS.length - 1][1];
}

/** Moisture ramp (NOAA dew-point convention: dry tan/brown → neutral → moist
 *  green → saturated teal). */
const RH_STOPS: Array<[number, [number, number, number]]> = [
  [0,    [166, 124, 82]],  // parched tan
  [0.35, [203, 185, 138]], // dry straw
  [0.55, [200, 208, 190]], // neutral
  [0.75, [110, 185, 120]], // moist green
  [1,    [40, 139, 139]],  // saturated teal
];
export function humidityColor(rh01: number): [number, number, number] {
  const v = Math.max(0, Math.min(1, rh01));
  for (let i = 1; i < RH_STOPS.length; i++) {
    if (v <= RH_STOPS[i][0]) {
      const [t0, c0] = RH_STOPS[i - 1];
      const [t1, c1] = RH_STOPS[i];
      const f = (v - t0) / (t1 - t0);
      return [c0[0] + (c1[0] - c0[0]) * f, c0[1] + (c1[1] - c0[1]) * f, c0[2] + (c1[2] - c0[2]) * f];
    }
  }
  return RH_STOPS[RH_STOPS.length - 1][1];
}

/** Categorical weather-class overlay colors [r,g,b,a] (radar-app style —
 *  precipitation saturated, clear nearly transparent). */
export const WEATHER_CLASS_COLOR: Record<WeatherClass, [number, number, number, number]> = {
  clear:  [255, 228, 120, 26],
  cloudy: [148, 158, 172, 120],
  fog:    [222, 228, 235, 140],
  sandstorm: [206, 152, 78, 170],
  rain:   [64, 128, 235, 170],
  storm:  [122, 66, 220, 200],
  snow:   [235, 245, 255, 190],
};

/**
 * Fill ONE row of an N×N RGBA weather-layer image over world rect
 * (x0,z0)+(w,h) — the World Atlas overlay samples through this (pure, so the
 * resolver harness proves the exact pixels the map shows).
 */
export function sampleWxRow(
  seed: string, mode: 'temp' | 'weather' | 'humidity', row: number, N: number,
  x0: number, z0: number, w: number, h: number, tSec: number, tod: number,
  out: Uint8ClampedArray,
): void {
  for (let i = 0; i < N; i++) {
    const wx = x0 + ((i + 0.5) / N) * w;
    const wz = z0 + ((row + 0.5) / N) * h;
    const m = meteoAt(seed, wx, wz, tSec, tod);
    let r: number, g: number, b: number, a: number;
    if (mode === 'temp') { [r, g, b] = tempColor(m.temperatureC); a = 150; }
    else if (mode === 'humidity') { [r, g, b] = humidityColor(m.humidity01); a = 150; }
    else { [r, g, b, a] = WEATHER_CLASS_COLOR[classifyMeteo(m)]; }
    const o = (row * N + i) * 4;
    out[o] = r; out[o + 1] = g; out[o + 2] = b; out[o + 3] = a;
  }
}

/** Magnus dew point (a = 17.62, b = 243.12 °C; ±0.35 °C over −45..60 °C). */
export function dewPoint(tempC: number, rh01: number): number {
  const rh = Math.max(0.01, Math.min(1, rh01));
  const a = 17.62, b = 243.12;
  const gamma = Math.log(rh) + (a * tempC) / (b + tempC);
  return (b * gamma) / (a - gamma);
}

// ─── The resolved (render-facing) weather ─────────────────────────────────────

export interface ResolvedWeather {
  cloudCoverage: number; cloudDensity: number; cloudTintHex: string;
  fogDensity: number; particleKind: 'none' | 'rain' | 'snow' | 'dust'; particleDensity: number;
  /** The blended biome floor inside `fogDensity` (clear air, THE VISIBILITY LAW) — the weather's own haze is the rest. */
  clearAirDensity: number;
  wetness: number; windStrength: number;
  /** Blowing dust 0..1 (a sandstorm): tints the fog, dims the sun, drives the dust emitters. */
  dust: number;
  /** The fog's tint under dust (null = the sky's own). */
  fogTintHex: string | null;
  /** How much the sun is dimmed (0..1) by the dust wall. */
  sunDim: number;
}

/** A REAL rain or snow sky is 7–8 oktas of nimbostratus: precipitation forces this much cover (× intensity on top). */
export const PRECIP_COVER_FLOOR = 0.9;
export const PRECIP_COVER_GAIN = 0.1;
/** The fog tint of blowing dust (an orange-brown haboob wall). */
export const DUST_FOG_TINT = '#b8834a';
/** The overcast deck under rain (nimbostratus grey) and under snow (a cooler grey). */
const RAIN_DECK_TINT = '#8e959e';
const SNOW_DECK_TINT = '#aeb6c0';
const DUST_DECK_TINT = '#c8a66f';

// A live-ops skyState.weather string → pinned derived outputs.
const OVERRIDE_INTENSITY: Record<string, number> = { clear: 0.0, cloudy: 0.55, fog: 0.5, rain: 0.75, storm: 1.0, snow: 0.8, sandstorm: 0.9 };

/**
 * Render-facing weather at (x, z). `t` MUST be a shared clock (wall-clock
 * seconds) so every client sees the same fronts; `tod` is the 0..1 time of
 * day (defaults to noon for callers that predate the meteo layer).
 */
export function resolveWeather(seed: string, x: number, z: number, t: number, override?: string, tod = 0.5): ResolvedWeather {
  const w = biomeWeights(seed, x, z);
  let coverage = 0, density = 0, fog = 0, wetness = 0, baseRH = 0;
  let tintR = 0, tintG = 0, tintB = 0;
  for (const b of Object.keys(w) as Biome[]) {
    const bw = w[b], p = BIOME_WEATHER[b];
    coverage += p.cloudCoverage * bw; density += p.cloudDensity * bw; fog += p.fogDensity * bw;
    wetness += p.wetness * bw; baseRH += p.baseRH * bw;
    const c = hexToRgb(p.cloudTintHex); tintR += c.r * bw; tintG += c.g * bw; tintB += c.b * bw;
  }
  const m = meteoAt(seed, x, z, t, tod);

  let particleKind = m.kind as 'none' | 'rain' | 'snow' | 'dust';
  let precip = m.precip01;
  let fogX = m.fog01;
  let dust = m.dust01;
  // The meteo field modulates the biome aesthetic floor: humidity above the
  // biome's norm thickens the sky, below it clears the sky.
  let coverage2 = clamp01(coverage + (m.humidity01 - baseRH) * 1.1 + precip * 0.12);
  let windStrength = m.wind01;

  let pinnedDry = false;
  if (override && override in OVERRIDE_INTENSITY) {
    // PINNED live-ops/debug event: intensity, kind, and wind are forced (a
    // 'snow' event snows even on warm plains; a forced storm is windy
    // EVERYWHERE); coverage/fog shift with the pin. The wind pins carry the
    // historical weather_profile spec values.
    const intensity = OVERRIDE_INTENSITY[override];
    const shift = (intensity - 0.5) * 2 * 0.18;
    coverage2 = clamp01(coverage2 + shift);
    if (override === 'snow') { particleKind = 'snow'; precip = 0.8; coverage2 = Math.max(coverage2, 0.5); windStrength = 0.45; }
    else if (override === 'rain') { particleKind = 'rain'; precip = 0.75; coverage2 = Math.max(coverage2, 0.5); windStrength = 0.6; }
    else if (override === 'storm') { particleKind = particleKind === 'snow' ? 'snow' : 'rain'; precip = 1; coverage2 = Math.max(coverage2, 0.62); windStrength = 0.85; }
    else if (override === 'sandstorm') {
      // Pinned everywhere by live-ops, but DUST ONLY WHERE THE DESERT IS: outside
      // it the same pin is a dry, windy, hazy sky (the front's edge).
      particleKind = 'none'; precip = 0;
      // A spot that is half desert (the biome blend at any dune's edge) gets the
      // full storm; the storm fades out only where the desert really ends.
      dust = Math.max(dust, smooth(0.08, 0.4, m.dustW) * 0.92);
      windStrength = Math.max(windStrength, 0.55 + 0.35 * m.dustW);
      fogX = Math.max(fogX, 0.12);
    }
    else {
      particleKind = 'none'; precip = 0;
      if (override === 'fog') { fogX = Math.max(fogX, 0.85); windStrength = 0.12; }
      else if (override === 'clear') { fogX = 0; windStrength = 0.1; pinnedDry = true; coverage2 = Math.min(coverage2, 0.1); }
      else windStrength = 0.35; // cloudy
    }
  }
  if (coverage2 < 0.25) { particleKind = 'none'; precip = 0; } // no rain under a near-clear sky
  if (particleKind === 'none' || precip <= 0.02) { precip = 0; if (particleKind !== 'dust') particleKind = 'none'; }
  // A sandstorm is dry: dust wins over a trace of precipitation, never over real rain.
  if (dust > 0.05 && precip <= 0.15) { particleKind = 'dust'; precip = 0; } else dust = precip > 0.15 ? 0 : dust;
  if (particleKind !== 'dust') dust = 0;

  // THE CLOUDS COME WITH THE RAIN (2026-09-11): precipitation falls from a
  // nimbostratus deck — 7–8 oktas — never from a half-clear sky. Cover is
  // floored by the intensity, the deck thickens and greys (cooler under snow).
  const storm = precip > 0.85;
  if (precip > 0) coverage2 = Math.max(coverage2, PRECIP_COVER_FLOOR + PRECIP_COVER_GAIN * precip);
  let tint = { r: tintR, g: tintG, b: tintB };
  if (precip > 0) tint = mixRgb(tint, hexToRgb(particleKind === 'snow' ? SNOW_DECK_TINT : RAIN_DECK_TINT), 0.5 + 0.5 * precip);
  if (dust > 0) { coverage2 = Math.max(coverage2, 0.35 * dust); tint = mixRgb(tint, hexToRgb(DUST_DECK_TINT), 0.8 * dust); }
  return {
    cloudCoverage: coverage2,
    cloudDensity: density * (storm ? 1.25 : 1) * (1 + 0.8 * precip),
    cloudTintHex: rgbToHex(tint.r, tint.g, tint.b),
    // Fog: biome floor + meteo fog + precipitation HAZE (the far-field read of
    // rain/snow beyond the particle volumes — distance greys out under precip;
    // heavy snow is a whiteout: Rasmussen 1999 puts heavy snow under 400 m) +
    // the DUST WALL (a haboob cuts visibility under a few hundred metres).
    // Rain (2026-09-27): fogV(2 km) at full intensity — heavy rain reads 1–2 km, light rain 5–8 km; the old
    // 0.0028 was a 700 m whiteout under any rain. Snow keeps Rasmussen's 250–440 m; a haboob 165 m.
    fogDensity: Math.max(0, fog + fogX * 0.012 + precip * (particleKind === 'snow' ? 0.0045 + 0.0035 * precip : fogV(2)) + dust * 0.012),
    // The biome's clear-air share of the density (AtmosphereModule removes what the aerial froxels already draw).
    clearAirDensity: fog,
    particleKind,
    particleDensity: particleKind === 'dust' ? clamp01(dust) : clamp01(precip),
    // A pinned 'clear' event dries surfaces to the biome floor; otherwise
    // ambient humidity keeps a light sheen and rain soaks. Dust dries everything.
    wetness: pinnedDry || dust > 0.3
      ? clamp01(wetness * 0.3)
      : clamp01(wetness * 0.4 + (particleKind === 'rain' ? precip * 0.85 : 0) + m.humidity01 * 0.15),
    windStrength: clamp01(windStrength + dust * 0.3),
    dust,
    fogTintHex: dust > 0.02 ? DUST_FOG_TINT : null,
    sunDim: clamp01(dust * 0.75),
  };
}

/**
 * Precedence for the weather `override`: a dev/admin Local override (the debug
 * menu's `setDebugWeather`) wins over the live-ops Commons override
 * (`skyState.weather`). `null` debug = no local override → fall through to the
 * commons value (which is itself `undefined` when unset → the meteo field).
 */
export function pickWeatherOverride(debug: string | null, commons: string | undefined): string | undefined {
  return debug ?? commons;
}

/**
 * Per-field numeric lerp of ResolvedWeather; cloudTintHex and particleKind snap
 * to target. THE CLOUDS COME FIRST (2026-09-11): the deck thickens on a faster
 * clock than the rain ramps — a sky greys over, then it rains; and when a front
 * leaves, the rain thins before the sky clears (a real front, not a switch).
 */
export function lerpResolved(a: ResolvedWeather, b: ResolvedWeather, k: number): ResolvedWeather {
  const L = (x: number, y: number, m = 1) => x + (y - x) * Math.min(1, k * m);
  return {
    cloudCoverage:  L(a.cloudCoverage,  b.cloudCoverage, 1.6),
    cloudDensity:   L(a.cloudDensity,   b.cloudDensity, 1.6),
    cloudTintHex:   b.cloudTintHex,   // snap: hex-lerp is not worth the complexity
    fogDensity:     L(a.fogDensity,    b.fogDensity),
    clearAirDensity: L(a.clearAirDensity, b.clearAirDensity),
    particleKind:   b.particleKind,   // snap: emitter-kind change is already guarded by setEmitter
    particleDensity:L(a.particleDensity, b.particleDensity, 0.45),
    wetness:        L(a.wetness,        b.wetness),
    windStrength:   L(a.windStrength,   b.windStrength),
    dust:           L(a.dust,           b.dust, 0.7),
    fogTintHex:     b.fogTintHex,
    sunDim:         L(a.sunDim,         b.sunDim, 0.7),
  };
}

// ─── Small pure helpers ───────────────────────────────────────────────────────

function clamp01(v: number): number { return v < 0 ? 0 : v > 1 ? 1 : v; }
function smooth(e0: number, e1: number, v: number): number {
  const t = clamp01((v - e0) / (e1 - e0));
  return t * t * (3 - 2 * t);
}
function hashSeed(s: string): number { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return (h >>> 0) / 4294967296; }

/** 2D value noise with a murmur3-finalized lattice hash (the avalanche law —
 *  raw FNV under-stirs the last-fed coordinate and bands). Smoothstep-interpolated. */
function vnoise(seed: string, u: number, v: number, salt: string): number {
  const x0 = Math.floor(u), z0 = Math.floor(v);
  const fx = u - x0, fz = v - z0;
  const sx = fx * fx * (3 - 2 * fx), sz = fz * fz * (3 - 2 * fz);
  const a = latticeHash(seed, x0, z0, salt), b = latticeHash(seed, x0 + 1, z0, salt);
  const c = latticeHash(seed, x0, z0 + 1, salt), d = latticeHash(seed, x0 + 1, z0 + 1, salt);
  return a + (b - a) * sx + (c - a) * sz + (a - b - c + d) * sx * sz;
}
function latticeHash(seed: string, ix: number, iz: number, salt: string): number {
  let h = 2166136261;
  const s = `${seed}|${salt}`;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  h ^= ix | 0; h = Math.imul(h, 16777619);
  h ^= iz | 0; h = Math.imul(h, 16777619);
  // murmur3 fmix32 finalizer — without it the last-fed coord bands (hash law).
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}
function mixRgb(a: { r: number; g: number; b: number }, b: { r: number; g: number; b: number }, t: number): { r: number; g: number; b: number } {
  const k = clamp01(t);
  return { r: a.r + (b.r - a.r) * k, g: a.g + (b.g - a.g) * k, b: a.b + (b.b - a.b) * k };
}
function hexToRgb(hex: string): { r: number; g: number; b: number } { const n = parseInt(hex.slice(1), 16); return { r: ((n >> 16) & 255) / 255, g: ((n >> 8) & 255) / 255, b: (n & 255) / 255 }; }
function rgbToHex(r: number, g: number, b: number): string { const h = (v: number) => Math.round(clamp01(v) * 255).toString(16).padStart(2, '0'); return `#${h(r)}${h(g)}${h(b)}`; }
